import clsx from 'clsx'
import { useState } from 'react'
import { useIntl } from 'react-intl'
import { yupResolver } from '@hookform/resolvers/yup'
import { FormProvider, useForm } from 'react-hook-form'

import { Button } from '../Button'
import { SearchInput } from '../SearchInput'
import { expertSkillsSchema } from '../../validators/expertSkillsSchema'
import { CheckboxCombo } from './components/CheckboxCombo'
import { useExpertSkills } from './useExpertSkills'
import { Spinner } from '../Spinner'

interface Props {
  expertId: number
}

export const ExpertSkillsMobile = ({ expertId }: Props) => {
  const [currentSkills, setCurrentSkills] = useState<'area' | 'domain'>('area')

  const { formatMessage } = useIntl()
  const {
    expert,
    isLoadingExpertSkills,
    isLoadingAreaSkills,
    isLoadingDomainSkills,
    filteredAreaSkills,
    filteredDomainSkills,
    areaSkills,
    domainSkills,
    translationLabel,
    isUpdatingSkills,
    updateSkills,
    handleSearchAreaSkills,
    handleSearchDomainSkills,
  } = useExpertSkills(expertId)

  const methods = useForm({
    resolver: yupResolver(expertSkillsSchema),
    defaultValues: {
      areaSkills: [] as any[],
      domainSkills: [] as any[],
    },
  })

  return (
    <div className="md:w-full lg:px-52">
      <header className="mb-6">
        <h2 className="text-base font-bold">
          {formatMessage({ id: 'SKILLS' })}
        </h2>
        <p className="text-base font-normal">
          {formatMessage({ id: 'ABLE_TO_TALK_ABOUT' })}
        </p>
      </header>
      <header className="flex w-full mb-6 rounded-lg bg-grey-300">
        <button
          className={clsx(
            'w-full py-3 flex-1 rounded-lg',
            currentSkills === 'area'
              ? 'bg-primary-light text-white'
              : 'bg-grey-300'
          )}
          onClick={() => setCurrentSkills('area')}
        >
          {formatMessage({ id: 'AREA' })}
        </button>
        <button
          className={clsx(
            'w-full py-3 flex-1 rounded-lg',
            currentSkills === 'domain'
              ? 'bg-primary-light text-white'
              : 'bg-grey-300'
          )}
          onClick={() => setCurrentSkills('domain')}
        >
          {formatMessage({ id: 'DOMAIN' })}
        </button>
      </header>
      <div className="flex justify-between">
        {currentSkills === 'area' && (
          <div className="flex flex-col w-full rounded-2xl">
            <div className="flex flex-row items-center justify-between w-full h-full mb-6">
              <SearchInput
                className="w-full"
                placeholder={formatMessage({ id: 'SEARCH_AREA' })}
                onSearch={handleSearchAreaSkills}
              />
            </div>

            {!isLoadingExpertSkills && !isLoadingAreaSkills ? (
              <FormProvider {...methods}>
                <form
                  className="flex flex-col w-full max-h-full"
                  id="area-form"
                  onSubmit={methods.handleSubmit(updateSkills)}
                >
                  {areaSkills && (
                    <CheckboxCombo
                      name="areaSkills"
                      options={filteredAreaSkills || areaSkills}
                      checkedOptions={expert?.areaSkills || []}
                      translationLabel={translationLabel}
                    />
                  )}

                  <Button
                    form="area-form"
                    type="submit"
                    className="w-full px-6 mt-6"
                    isLoading={methods.formState.isSubmitting}
                    disabled={!methods.formState.isDirty}
                  >
                    {formatMessage({ id: 'SAVE' })}
                  </Button>
                </form>
              </FormProvider>
            ) : (
              <Spinner />
            )}
          </div>
        )}

        {currentSkills === 'domain' && (
          <div className="flex flex-col w-full rounded-2xl">
            <div className="flex flex-row items-center justify-between w-full h-full mb-6">
              <SearchInput
                className="w-full"
                placeholder={formatMessage({ id: 'SEARCH_DOMAIN' })}
                onSearch={handleSearchDomainSkills}
              />
            </div>
            {!isLoadingExpertSkills && !isLoadingDomainSkills ? (
              <FormProvider {...methods}>
                <form
                  className="flex flex-col w-full max-h-full"
                  id="area-form"
                  onSubmit={methods.handleSubmit(updateSkills)}
                >
                  {domainSkills && (
                    <CheckboxCombo
                      name="domainSkills"
                      options={filteredDomainSkills || domainSkills}
                      checkedOptions={expert?.domainSkills || []}
                      translationLabel={translationLabel}
                    />
                  )}

                  <Button
                    form="area-form"
                    isLoading={isUpdatingSkills}
                    type="submit"
                    className="w-full px-6 mt-6"
                    disabled={!methods.formState.isDirty}
                  >
                    {formatMessage({ id: 'SAVE' })}
                  </Button>
                </form>
              </FormProvider>
            ) : (
              <Spinner />
            )}
          </div>
        )}
      </div>
    </div>
  )
}
